/**
 * 플래너 상담 지도(`PlannerPage`) 안의 날짜 선택 캘린더용 휴무일 결정 로직.
 *
 * 캘린더는 "근무일" 목록(`workDates`)을 받아 그 외 날짜를 비활성(disabled = 휴무)
 * 처리한다. 그러나 페이지 초기 마운트 직후에는 `workDates` 가 아직 SWR 로딩 중이라
 * 빈 배열로 들어와, 모든 날짜가 활성 상태로 보여 휴무/근무 마킹이 사라지는
 * 문제가 있다 (see: 버그 1).
 *
 * 본 함수는 다음 우선순위로 비활성 여부를 결정한다.
 *  1. workDates 가 이미 로드되었다면 → workDates 에 없는 날짜는 휴무.
 *  2. workDates 는 로딩 중이지만 "오늘" 의 단일-일 근무 상태가 별도로 로드되어
 *     있다면 → 오늘 한정으로 그 결과를 반영. (다른 날짜는 가짜로 채우지 않음.)
 *  3. 둘 다 모르면 → 기본값으로 비활성하지 않음 (마킹 미표시).
 */
export interface PlannerCalendarDisableState {
  /** SWR 에서 받은 근무일 목록. 로딩 중이면 [] 일 수 있음. */
  workDates: readonly string[];
  /** workDates 의 SWR fetch 가 완료되었는지 여부 (data !== undefined). */
  isWorkDatesLoaded: boolean;
  /** 오늘 날짜 (YYYY-MM-DD). 외부에서 dayjs 로 계산해 주입. */
  todayDateString: string;
  /**
   * 오늘 한정 근무 상태. 빠른 단일-일 endpoint 결과.
   *  - 'work': 오늘 근무 슬롯이 있음 → 활성
   *  - 'off' : 오늘 근무 슬롯이 없음 → 비활성(휴무)
   *  - null  : 아직 로드되지 않음
   */
  todayWorkStatus: 'work' | 'off' | null;
}

export function shouldDisableDateInPlannerCalendar(
  dateString: string,
  state: PlannerCalendarDisableState
): boolean {
  if (state.isWorkDatesLoaded) {
    return !state.workDates.includes(dateString);
  }
  if (dateString === state.todayDateString && state.todayWorkStatus !== null) {
    return state.todayWorkStatus === 'off';
  }
  return false;
}
